import { API } from "aws-amplify";
import moment from "moment";
import { applyLocationEmailData, getLessonAddressData, getLessonNote, getLessonNoteText, LOCATION_EMAIL_DATA } from "../../components/contstants/LocationEmailData";
import { buildAPIOptions } from "../../utils/appointmentUtils";

/**
 * Sends an email via the API.
 * @param {object} params - The email params payload.
 * @param {function} onRefreshStatus - Called with the active key on success.
 * @param {string} activeKey - The current active accordion key.
 * @param {function} setLoading - Called with true/false to set loading state.
 */
export async function sendEmail(params, onRefreshStatus, activeKey, setLoading) {
  setLoading(true);
  const options = await buildAPIOptions(params);
  API.post("APIGateway", "/sendemail", options)
    .then(function () {
      onRefreshStatus(activeKey);
    })
    .catch(function (error) {
      console.log(error);
      if (error.response) {
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
      } else if (error.request) {
        console.log(error.request);
      } else {
        console.log("Error", error.message);
      }
    });
}

/**
 * Generates a UUID v4.
 */
function uuidv4() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (
      c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16),
  );
}

/**
 * Sends the initial "thanks for signing up" email in bulk.
 * @param {object[]} emailRecipient - Array of recipients; empty array means "send to all unsent".
 * @param {object} state - Slice of component state needed: filteredAPIDateStudentResults, selectedRealDays.
 * @param {function} onSendEmail - The sendEmail function (already bound or wrapped).
 */
export async function sendInitalEmailBulk(emailRecipient, state, onSendEmail) {
  let emailRecipients = emailRecipient;
  if (emailRecipient.length === 0) {
    emailRecipients = state.filteredAPIDateStudentResults
      .filter((x) => Object.keys(x).length > 0 && x.constructor === Object)
      .filter((x) => x.emailInitalState === "Not Sent");
  }
  var dynamoDBDetails = [];
  let defaultTemplate = JSON.stringify({
    firstName: "tempFirst",
    lastName: "tempLast",
    package: "tempPackage",
    weekendStart: "tempStartDate",
    extraText: "",
    address: "",
    extraLocationText: "Road Test Location",
    roadTestAddressLine: "",
    lessonAddressLine: "",
  });
  var params = {
    Source: "Parkway Driving School<info@parkwaydrivingschool.com>",
    Template: "confirmOrder2",
    DefaultTemplateData: defaultTemplate,
    ReplyToAddresses: ["info@parkwaydrivingschool.com"],
    Destinations: [],
  };

  let lengthOfSelectedDays = state.selectedRealDays.length;
  for (let index = 0; index < emailRecipients.length; index++) {
    const formData = emailRecipients[index];

    var lessonText = "";
    var extraLocationText = "";
    var roadTestAddressLine = "";
    var lessonAddressLine = "";

    const hasLessons =
      formData.hasOwnProperty("scheduledLessons") &&
      formData.scheduledLessons.length > 0;
    var locationsOfTest = new Set();

    if (hasLessons) {
      lessonText = formData.scheduledLessons.map((lessonData) => {
        var lessonText =
          moment(lessonData.date).format("MM-DD-YY") +
          " at " +
          lessonData.slotName +
          " in " +
          "<span style=\"color:#FF0000\">" + lessonData.location + "</span>" +
          "<br>";
        return lessonText;
      }).join("");

      locationsOfTest = new Set(
        formData.scheduledLessons.map((lessonData) => {
          return lessonData.location;
        }),
      );
    }

    const isWestRoxburySplit = formData.location === "West Roxbury" && hasLessons;

    if (isWestRoxburySplit) {
      // Lessons and the road test happen at two different West Roxbury addresses.
      // Each address is already shown (and linked) right next to the line it belongs
      // to, so there's no separate "Locations" recap below — that would just repeat them.
      roadTestAddressLine =
        "<a href=\"https://maps.google.com/?q=48+Railroad+Street,+West+Roxbury\">48 Railroad Street, West Roxbury, MA 02132</a><br>";
      lessonAddressLine =
        "<a href=\"https://maps.google.com/?q=1752+Centre+Street,+West+Roxbury\">1752 Centre Street, West Roxbury, MA 02132</a><br>";
      extraLocationText = "";
    } else {
      const roadTestLocationData =
        LOCATION_EMAIL_DATA[formData.location] || {
          address: "Address TBA",
          googleMapUrl: "https://maps.google.com",
        };
      const lessonData = hasLessons
        ? getLessonAddressData([...locationsOfTest][0])
        : null;

      if (hasLessons && lessonData.address !== roadTestLocationData.address) {
        // The road test and the lessons are at two different addresses,
        // so show them as two separately labeled locations.
        extraLocationText =
          "Road Test Location:<br><a href=\"" + roadTestLocationData.googleMapUrl + "\">" + roadTestLocationData.address + "</a>" +
          "<br><br>Lesson Location:<br><a href=\"" + lessonData.googleMapUrl + "\">" + lessonData.address + "</a>";
      } else {
        extraLocationText =
          (hasLessons ? "Road Test & Lesson Location:" : "Road Test Location:") +
          "<br><a href=\"" + roadTestLocationData.googleMapUrl + "\">" + roadTestLocationData.address + "</a>";
      }
    }

    var weekendStartText = moment(state.selectedRealDays[0]).format(
      "dddd, MMMM Do YYYY",
    );
    if (lengthOfSelectedDays === 7) {
      weekendStartText =
        " Week of " +
        moment(state.selectedRealDays[0]).format("M/D") +
        " - " +
        moment(state.selectedRealDays[0]).add(6, "day").format("M/D") +
        " (Sat-Fri, Exact date tbd)";
    }

    var ReplacementTemplateData = {
      firstName: " " + formData.fName,
      lastName: formData.lName,
      package: " " + formData.selectedPackage,
      weekendStart: weekendStartText,
      extraText: lessonText,
      extraLocationText: extraLocationText,
      roadTestAddressLine: roadTestAddressLine,
      lessonAddressLine: lessonAddressLine,
      // extraLocationText above already embeds the location's address as a link;
      // leave these blank so the template's own address/googleMapUrl line (used
      // by older deployed versions of confirmOrder2) doesn't print it a second time.
      address: "",
      googleMapUrl: "",
    };
    console.log(formData);

    ReplacementTemplateData = JSON.stringify(ReplacementTemplateData);

    let dbDetails = {
      date: formData.date,
      timestamp: formData.timestamp,
    };
    dynamoDBDetails.push(dbDetails);
    var data = {
      Destination: {
        ToAddresses: [formData.emailAddress],
      },
      ReplacementTemplateData: ReplacementTemplateData,
    };

    if (
      formData.parentEmailAddress &&
      formData.parentEmailAddress.length > 0
    ) {
      data["Destination"]["CcAddresses"] = [formData.parentEmailAddress];
    }
    params.Destinations.push(data);
  }
  let finalData = {
    params: params,
    dynamoDBDetails: dynamoDBDetails,
  };
  if (finalData.dynamoDBDetails.length > 0) {
    onSendEmail(finalData);
  } else {
    console.log("No emails to send");
  }
}

/**
 * Sends road test slot or time emails in bulk.
 * @param {boolean} useSlotTime - Whether to use slot time or unslot time.
 * @param {object[]} input - Direct recipients array (length 1) or empty to auto-select.
 * @param {object} state - Slice of state: selectedLocation, unslotedMembers, slots, dayOfExam.
 * @param {function} onSendEmail - The sendEmail function.
 */
export async function sendRoadTestSlotOrTimeEmailsBulk(useSlotTime, input = [], state, onSendEmail) {
  let emailRecipients = [];
  if (input.length === 1) {
    emailRecipients = input;
  } else {
    if (useSlotTime) {
      if (
        state.selectedLocation.includes("RMV Site") ||
        state.selectedLocation.includes("Braintree - Granite St")
      ) {
        emailRecipients = state.unslotedMembers
          .filter(
            (x) => Object.keys(x).length > 0 && x.constructor === Object,
          )
          .filter((x) => x.emailRoadTestSlotState === "Not Sent");
      } else {
        emailRecipients = state.slots
          .filter(
            (x) =>
              Object.keys(x.value).length > 0 &&
              x.value.constructor === Object,
          )
          .filter((x) => x.value.emailRoadTestSlotState === "Not Sent")
          .map((x) => x.value);
      }
    } else {
      emailRecipients = state.unslotedMembers.filter(
        (x) => x.emailRoadTestSlotState === "Not Sent",
      );
    }
  }

  var dynamoDBDetails = [];

  let defaultTemplate = JSON.stringify({
    dateTime: "PLACE_DATE",
    linkLocation: "https://bostonroadtests.com/confirmation",
    address: "",
    googleMapUrl: "",
    lessonNote: "",
    lessonNoteText: "",
  });
  var params = {
    Source: "Parkway Driving School<info@parkwaydrivingschool.com>",
    Template: "appointmentTemplate3",
    DefaultTemplateData: defaultTemplate,
    ReplyToAddresses: ["info@parkwaydrivingschool.com"],
    Destinations: [],
  };

  var finalTime = "";
  for (let index = 0; index < emailRecipients.length; index++) {
    var formData = emailRecipients[index];
    let token = uuidv4();
    finalTime =
      moment(state.dayOfExam).format("dddd, MMMM Do YYYY") +
      " at " +
      (useSlotTime
        ? (state.selectedLocation.includes("RMV Site") ||
           state.selectedLocation.includes("Braintree - Granite St"))
          ? formData.unslotTime
          : formData.slot
        : formData.unslotTime);
    if (!useSlotTime) {
      console.log(formData);
    }

    var ReplacementTemplateData = {
      dateTime: finalTime,
      linkLocation:
        "https://bostonroadtests.com/confirmation?dateStart=" +
        formData.date +
        "&token=" +
        token,
      extraLocationInstructions: "",
      extraLocationInstructionsText: "",
    };
    applyLocationEmailData(formData.location, ReplacementTemplateData);
    // Students with lessons at a different address than their road test also get the lesson address.
    ReplacementTemplateData["lessonNote"] = getLessonNote(
      formData.location,
      formData.scheduledLessons,
    );
    ReplacementTemplateData["lessonNoteText"] = getLessonNoteText(
      formData.location,
      formData.scheduledLessons,
    );

    let dbDetails = {
      date: formData.date,
      timestamp: formData.timestamp,
      token: token,
    };
    dynamoDBDetails.push(dbDetails);
    var data = {
      Destination: {
        ToAddresses: [formData.emailAddress],
      },
      ReplacementTemplateData: JSON.stringify(ReplacementTemplateData),
    };
    if (
      formData.parentEmailAddress &&
      formData.parentEmailAddress.length > 0
    ) {
      data["Destination"]["CcAddresses"] = [formData.parentEmailAddress];
    }
    params.Destinations.push(data);
  }
  let finalData = {
    params: params,
    dynamoDBDetails: dynamoDBDetails,
  };
  if (finalData.dynamoDBDetails.length > 0) {
    onSendEmail(finalData);
  } else {
    console.log("No emails to send");
  }
}

/**
 * Sends road test result emails in bulk.
 * @param {boolean} slotMode - Whether to use slots (true) or unslotedMembers (false).
 * @param {string} template - The SES template name.
 * @param {function} filterSlotted - Filter function for slotted students.
 * @param {function} filterUnslotted - Filter function for unslotted members.
 * @param {object} state - Slice of state: slots, unslotedMembers.
 * @param {function} onSendEmail - The sendEmail function.
 * @param {function} setEmailError - Clears the email error (called with "").
 */
export async function sendRoadTestResultEmailsBulk(
  slotMode,
  template,
  filterSlotted,
  filterUnslotted,
  state,
  onSendEmail,
  setEmailError,
) {
  setEmailError("");
  var dynamoDBDetails = [];
  var params = {
    Source: "Parkway Driving School<info@parkwaydrivingschool.com>",
    Template: template,
    DefaultTemplateData: '{"firstName": "templateFirstNameholder"}',
    ReplyToAddresses: ["info@parkwaydrivingschool.com"],
    Destinations: [],
  };
  const emailRecipients = slotMode
    ? state.slots
        .filter(
          (x) =>
            Object.keys(x.value).length > 0 && x.value.constructor === Object,
        )
        .filter(filterSlotted)
        .map((x) => x.value)
    : state.unslotedMembers.filter(filterUnslotted);
  for (let index = 0; index < emailRecipients.length; index++) {
    const formData = emailRecipients[index];
    const ReplacementTemplateData = JSON.stringify({
      firstName: formData.fName,
    });
    dynamoDBDetails.push({
      date: formData.date,
      timestamp: formData.timestamp,
    });
    const data = {
      Destination: { ToAddresses: [formData.emailAddress] },
      ReplacementTemplateData: ReplacementTemplateData,
    };
    if (
      formData.parentEmailAddress &&
      formData.parentEmailAddress.length > 0
    ) {
      data["Destination"]["CcAddresses"] = [formData.parentEmailAddress];
    }
    params.Destinations.push(data);
  }
  onSendEmail({ params, dynamoDBDetails });
}

/**
 * Checks and formats the exam email date, storing it in state.
 * @param {object} state - Slice of state: dayOfExam.
 * @param {function} setEmailSendDateData - setState partial: (value) => setState({ emailSendDateData: value, EmailError: "" }).
 */
export function checkEmailDate(state, setEmailSendDateData) {
  let examDate = state.dayOfExam;
  var finalTime = moment(examDate).format("dddd, MMMM Do YYYY");
  setEmailSendDateData(finalTime);
}
